import { money, type Money } from "./money.ts";

/**
 * What EXP-002 answers about one ride, and what each of the seven journey screens may draw from it.
 *
 * The types below are EXP-002's own response, restated rather than imported: `packages/api-client`
 * generates from the published contracts and a channel consumes those, but the experience tier's
 * aggregates are not in the generated client today, so this is the hand-written edge of it. Every
 * field is copied from `services/experience/customer-bff/src/application/ride-journey.ts` and
 * `.../domain/ride-journey.ts`, including the absence vocabularies, because the whole value of this
 * component is that an absence has a **reason** and a screen that collapsed four reasons into one
 * would throw the reason away at the last step.
 *
 * ## The four absences this file exists to not paper over
 *
 * `tests/contract/mobile-screen-support.test.ts` names, per screen, the drawn element with no
 * source. EXP-002 publishes each as a typed reason, and this module's job is to carry it to a
 * rendering rather than to a fallback:
 *
 *   - **`tripId: null` with `tripAbsence: not_joinable`.** Nothing published joins a ride request to
 *     the trip created from it (R-28). `6 Driver coming`, `7 In trip` and `8 Complete` are *about* a
 *     trip, and the measurement grades them degraded only by granting the screen a `tripId` it
 *     cannot get. So those three draw the designed absence rather than a journey.
 *   - **`finalFare: null`**, with `finalFareAbsence` separating `trip_not_complete` from
 *     `no_published_route`. A ride still running has no final fare because it has not finished; a
 *     finished one has none because nothing publishes a completed trip's charged total. Only the
 *     second is a gap somebody should fix, and `8b Fare differs` is entirely about that figure.
 *   - **`vehicle: null` with `vehicleAbsence: not_joinable`.** `KDA 417Q` and `Toyota Axio · silver`
 *     have no path from the driver who is coming to the car they are driving.
 *   - **`dispatch: null` with `dispatchAbsence: no_published_route`.** Which is why `5 Finding` and
 *     `5b No driver` are not built at all rather than built empty.
 *
 * And one that is not an absence but a judgement: **`stage` is ten-valued including `unknown`**. A
 * named trip that could not be read must never fall back to `requested`, which would draw
 * _"Finding your driver"_ over a ride already in progress. EXP-002 refuses that fallback and so does
 * every function below.
 */

/* ------------------------------------------------------------------- EXP-002's response, as sent */

export type JourneyStage =
  | "requested"
  | "cancelled"
  | "assigned"
  | "driver_en_route"
  | "driver_arrived"
  | "waiting"
  | "in_trip"
  | "completed"
  | "no_show"
  | "unknown";

/** Which service's statement the stage rests on, published beside it rather than inferred from it. */
export type StageEvidence = "trip" | "request" | "none";

export type TripAbsence = "not_joinable" | "unreadable" | "not_this_journey";
export type DispatchAbsence = "no_published_route";
export type FinalFareAbsence = "trip_not_complete" | "no_published_route";
export type DriverAbsence = "no_trip" | "unreadable" | "not_this_trip";
export type PositionAbsence =
  "no_trip" | "unreadable" | "not_this_trip" | "tracking_ended" | "no_sample";
export type EtaAbsence =
  "no_position" | "no_destination" | "no_mode" | "unreadable" | "stage_has_no_leg";
export type EstimateAbsence = "no_quote_identifier" | "unreadable";
export type EtaLeg = "to_pickup" | "to_dropoff";
export type TravelMode = "car" | "motorcycle" | "bicycle" | "walking";

export interface Coordinate {
  readonly latitude: number;
  readonly longitude: number;
}

export interface EstimateView {
  readonly fare: Money;
  readonly source: "quote" | "request";
  readonly lineItems: readonly { readonly code: string; readonly amountMinor: number }[];
  readonly expiresAt: string;
  /** Derived by EXP-002 against **its own** instant, so a phone's clock cannot decide the screen. */
  readonly expired: boolean;
  /** True when MOB-02 and MOB-03 both answered and their fares differ. Should be impossible. */
  readonly disagrees: boolean;
}

export interface DriverView {
  readonly driverId: string;
  /** DRV-01's name, or null when that layer did not answer. Never the identifier. */
  readonly displayName: string | null;
  /** RAT-03's average as the integer basis points it publishes. Never pre-divided. */
  readonly ratingBasisPoints: number | null;
  readonly ratingObservationCount: number | null;
  /** False when RAT-03 called its own aggregate stale, or did not answer. */
  readonly ratingConfirmed: boolean;
}

export interface PositionView {
  readonly at: Coordinate;
  readonly accuracyMetres: number;
  /** When the driver's device took the sample, which is the clock that matters. */
  readonly sampledAt: string;
  /** When TRP-02 last changed the projection, which is a different clock. */
  readonly projectedAt: string;
  readonly ageMilliseconds: number;
  /** Whether the sample is inside the freshness window, and therefore drawable as current. */
  readonly confirmed: boolean;
  readonly freshnessWindowMs: number;
}

export interface EtaView {
  readonly leg: EtaLeg;
  readonly distanceMetres: number;
  readonly durationSeconds: number;
  readonly calculatedAt: string;
  readonly freshUntil: string;
  readonly from: Coordinate;
  readonly to: Coordinate;
  readonly mode: TravelMode;
}

export interface AssignmentView {
  readonly assignmentId: string;
  readonly assignedAt: string;
  readonly active: boolean;
  readonly releasedAt: string | null;
}

/** One layer's verdict, published beside the rows it did or did not produce. */
export interface SourceReport {
  readonly layer: string;
  readonly service: string;
  readonly status: "ok" | "forbidden" | "unauthorized" | "unavailable" | "timeout" | "invalid";
  readonly scopes: readonly string[];
  readonly code?: string;
  readonly truncated?: boolean;
}

export interface CountFloor {
  readonly layer: string;
  readonly service: string;
  readonly reason: "truncated" | "did_not_answer" | "bounded";
}

/** What one request to EXP-002's journey aggregate answers. */
export interface JourneyView {
  readonly tenantId: string;
  readonly customerId: string;
  readonly rideRequestId: string;
  /** When the aggregate was assembled. One instant for all eight layers. */
  readonly observedAt: string;
  readonly stage: JourneyStage;
  readonly stageEvidence: StageEvidence;
  readonly requestStatus: "requested" | "cancelled" | null;
  readonly cancellation: { readonly reasonCode: string; readonly cancelledAt: string } | null;
  readonly tripId: string | null;
  readonly tripAbsence?: TripAbsence;
  readonly estimate: EstimateView | null;
  readonly estimateAbsence?: EstimateAbsence;
  readonly finalFare: Money | null;
  readonly finalFareAbsence: FinalFareAbsence;
  readonly dispatch: null;
  readonly dispatchAbsence: DispatchAbsence;
  readonly assignment: AssignmentView | null;
  readonly driver: DriverView | null;
  readonly driverAbsence?: DriverAbsence;
  readonly vehicle: null;
  readonly vehicleAbsence: "not_joinable";
  readonly position: PositionView | null;
  readonly positionAbsence?: PositionAbsence;
  readonly eta: EtaView | null;
  readonly etaAbsence?: EtaAbsence;
  readonly trailPoints: number;
  /** Whether the counts on this screen may be drawn as totals. R-04: none of them may, today. */
  readonly countsAreTotals: boolean;
  readonly countFloors: readonly CountFloor[];
  readonly sources: readonly SourceReport[];
  /** True when any layer that was read did not answer. A screen must say so. */
  readonly degraded: boolean;
}

/* -------------------------------------------------------------------- the design's own vocabulary */

/**
 * The seven chip classes `10 Rider App.dc.html` defines, and no eighth.
 *
 * `CH` and `GL` in the design file: `idle`, `active`, `caution`, `closed`, `unknown`, `failed`,
 * `done`, each with its own fill, its own foreground and its own glyph shape. A state is shown by a
 * glyph **and** a word, never by a colour alone.
 */
export type ChipClass = "idle" | "active" | "caution" | "closed" | "unknown" | "failed" | "done";

export interface Chip {
  readonly class: ChipClass;
  readonly label: string;
}

/**
 * The stage, as the design's own chip.
 *
 * The design writes the platform's own status words into the chip verbatim — `driver_en_route`,
 * `in_trip`, `completed` — so the label is the stage and not a translation of it. What this function
 * decides is the **class**, and the one that earns its keep is `unknown`: the design's dashed pill
 * with a literal `?`, which is what the tenth value of `stage` draws. A ride whose stage could not be
 * confirmed must not take `active`'s blue half-circle, because that pill over a spinner is exactly
 * the over-claim R-09 and R-18 both record.
 *
 * `cancelled` takes `closed` and `no_show` takes `failed`, which are the design's own classes for a
 * thing that ended and a thing that went wrong; neither stage has a drawn screen in this file, and
 * both are reachable, so leaving them to a default would have been a colour chosen by omission.
 */
export function stageChip(stage: JourneyStage): Chip {
  switch (stage) {
    case "requested":
      return { class: "idle", label: "requested" };
    case "assigned":
    case "driver_en_route":
    case "driver_arrived":
    case "waiting":
    case "in_trip":
      return { class: "active", label: stage };
    case "completed":
      return { class: "done", label: "completed" };
    case "cancelled":
      return { class: "closed", label: "cancelled" };
    case "no_show":
      return { class: "failed", label: "no_show" };
    case "unknown":
      return { class: "unknown", label: "unknown" };
  }
}

/**
 * Whether the stage rests on a statement or on an inference, said in one short phrase.
 *
 * `stageEvidence` is `trip | request | none`, and `request` is weaker than it looks: MOB-03's status
 * vocabulary is `requested | cancelled` and **nothing moves it when a trip starts**, so a ride
 * request reads `requested` for the whole journey and for ever afterwards. A screen that drew that
 * as "we are still looking for your driver" would be wrong for most of a ride. So the evidence is
 * drawn beside the stage wherever the stage is the only thing on screen.
 */
export function stageEvidenceWords(evidence: StageEvidence): string {
  switch (evidence) {
    case "trip":
      return "The trip service says so.";
    case "request":
      return "Only your booking could be read, and a booking never says a ride has started.";
    case "none":
      return "Nothing could be read, so the stage is not known.";
  }
}

/** Why there is no trip on this answer, in the words the screen puts under its title. */
export function tripAbsenceWords(absence: TripAbsence | undefined): string {
  switch (absence) {
    case "not_joinable":
      return "Nothing published joins a booking to the trip made from it, so this app cannot find your trip from here.";
    case "unreadable":
      return "The trip service did not answer about the trip this link names.";
    case "not_this_journey":
      return "The trip this link names belongs to a different booking.";
    case undefined:
      return "No trip.";
  }
}

/** Why there is no driver, which is three facts and not one. */
export function driverAbsenceWords(absence: DriverAbsence | undefined): string {
  switch (absence) {
    case "no_trip":
      return "No trip could be joined, so no driver has been named.";
    case "unreadable":
      return "The driver's details could not be read.";
    case "not_this_trip":
      return "The profile that came back is not this trip's driver.";
    case undefined:
      return "No driver.";
  }
}

/** Why there is no position to draw, which is five facts and not one. */
export function positionAbsenceWords(absence: PositionAbsence | undefined): string {
  switch (absence) {
    case "no_trip":
      return "No trip could be joined, so there is nowhere to follow.";
    case "unreadable":
      return "The tracking service did not answer.";
    case "not_this_trip":
      return "The tracking that came back is for a different trip.";
    case "tracking_ended":
      return "Tracking for this trip has ended.";
    case "no_sample":
      return "The driver's phone has not reported a position yet.";
    case undefined:
      return "No position.";
  }
}

/** Why there is no arrival estimate. `stage_has_no_leg` is a fact about the ride, not a failure. */
export function etaAbsenceWords(absence: EtaAbsence | undefined): string {
  switch (absence) {
    case "stage_has_no_leg":
      return "Nobody is travelling yet, so there is nothing to estimate.";
    case "no_position":
      return "Without the driver's position there is nothing to measure from.";
    case "no_destination":
      return "The route this ride was priced for could not be read.";
    case "no_mode":
      return "The kind of vehicle this ride was priced for could not be read.";
    case "unreadable":
      return "The routing service did not answer.";
    case undefined:
      return "No estimate.";
  }
}

/** Why there is no final fare. The two reasons are not the same news and are not worded alike. */
export function finalFareAbsenceWords(absence: FinalFareAbsence): string {
  switch (absence) {
    case "trip_not_complete":
      return "Not until the ride ends";
    case "no_published_route":
      return "Not published";
  }
}

/* --------------------------------------------------------------------------- what a screen draws */

/**
 * `Arriving in 4 min`, or the reason there is no number.
 *
 * The design's headline on `6 Driver coming`. GEO-04's `durationSeconds` is the only source, and it
 * is reachable only when there is a verified trip, a position to measure from and a quote to measure
 * to — which is three things a journey with `tripAbsence: not_joinable` has none of.
 *
 * Rounded **up** to the next whole minute, and never to zero. A driver 20 seconds away is "arriving
 * in 1 min", not "arriving in 0 min"; and a rider who reads 0 and sees nobody concludes the app is
 * wrong rather than that the car is close.
 */
export function arrivalHeadline(view: JourneyView): { headline: string; isAbsence: boolean } {
  const eta = view.eta;
  if (eta === null) {
    return { headline: whyNoArrival(view), isAbsence: true };
  }
  const minutes = Math.max(1, Math.ceil(eta.durationSeconds / 60));
  return {
    headline:
      eta.leg === "to_pickup"
        ? `Arriving in ${String(minutes)} min`
        : `Arriving in about ${String(minutes)} min`,
    isAbsence: false,
  };
}

/**
 * The headline when there is no estimate, which names the **nearest** missing fact rather than the
 * last one.
 *
 * A rider told "the routing service did not answer" when the real problem is that this application
 * cannot find their trip at all would wait, and retry, and wait. The trip absence is the one worth
 * saying, so it is said first.
 */
function whyNoArrival(view: JourneyView): string {
  if (view.tripId === null) return "We can't find your trip";
  if (view.position === null) return "We don't know where your driver is";
  return "We can't work out your arrival";
}

/**
 * `19 km left`, from GEO-04's own distance.
 *
 * Kilometres to one decimal below 10 km and whole above it, which is what the design's two samples
 * do (`24 km`, `3.1 km`, `1.2 km`). Below 1 km it is metres, because "0.4 km left" on a phone
 * fifty seconds from a gate is a worse sentence than "400 m left".
 */
export function distanceLabel(metres: number): string {
  if (metres < 1000) return `${String(Math.round(metres / 10) * 10)} m`;
  const km = metres / 1000;
  return km < 10 ? `${km.toFixed(1)} km` : `${String(Math.round(km))} km`;
}

/**
 * `4.9` — the driver's rating, from RAT-03's integer basis points.
 *
 * `averageScoreBasisPoints` is the mean score times ten thousand over facts whose score units are
 * integers `0..5`. Dividing by the wrong power of ten is wrong by a factor of a hundred and would
 * look plausible either way, so the division is here with the reason attached rather than inline at a
 * call site.
 */
export function ratingLabel(basisPoints: number): string {
  return (basisPoints / 10_000).toFixed(1);
}

/**
 * The driver's name and rating, as `6 Driver coming`'s card writes them: `P. Njoroge · 4.9`.
 *
 * Four things can be missing independently and each is said rather than filled:
 *
 *   - no driver at all — because no trip could be joined;
 *   - a driver with no name — DRV-01 did not answer, and EXP-002 publishes `displayName: null`
 *     rather than the identifier, "because a BFF answering `displayName: 'drv_4410'` would be
 *     telling a client that DRV-01 published a name it did not";
 *   - a driver with no rating — RAT-03 did not answer;
 *   - a driver whose rating RAT-03 itself called **stale**, which is `ratingConfirmed: false`. That
 *     one is not drawn as a number at all. A stale 4.9 is a 4.9 a rider acts on, and R-23 is the
 *     recorded incident of a staleness flag being published and not consulted.
 */
export function driverLine(view: JourneyView): {
  readonly name: string | null;
  readonly rating: string | null;
  readonly ratingIsStale: boolean;
} {
  const driver = view.driver;
  if (driver === null) return { name: null, rating: null, ratingIsStale: false };
  const score = driver.ratingBasisPoints;
  return {
    name: driver.displayName,
    rating: score !== null && driver.ratingConfirmed ? ratingLabel(score) : null,
    ratingIsStale: score !== null && !driver.ratingConfirmed,
  };
}

/**
 * How a position may be drawn, which is three answers and not two.
 *
 * `position.confirmed` arrives with `sampledAt`, `projectedAt`, `ageMilliseconds` and
 * `freshnessWindowMs` precisely so that a screen does not have to decide this for itself — and so
 * that when it draws a stale fix it can say how stale. R-23 is the recorded fault: the dispatch live
 * map drew a driver as working because one clock was consulted and not the other, **twice**, and the
 * fix was not a better boolean but publishing what the boolean rested on.
 *
 * Design rule 5 settles the drawing: a confirmed fix is the blue marker with its halo; a stale one is
 * dashed grey with an accuracy circle; no fix at all is no marker.
 */
export type PositionConfidence = "confirmed" | "stale" | "absent";

export function positionConfidence(view: JourneyView): PositionConfidence {
  if (view.position === null) return "absent";
  return view.position.confirmed ? "confirmed" : "stale";
}

/** `last seen 4 min ago` — how old a stale fix is, from the age EXP-002 already computed. */
export function positionAgeLabel(position: PositionView): string {
  const seconds = Math.round(position.ageMilliseconds / 1000);
  if (seconds < 60) return `${String(seconds)}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${String(minutes)} min ago`;
  return `${String(Math.round(minutes / 60))} h ago`;
}

/**
 * The estimated fare, as the `Est.` half of the design's fare box.
 *
 * Null rather than a zero when EXP-002 could read neither immutable copy of it. `AGENTS.md` and
 * EXP-002's own `money()` both refuse to build a figure from half a payload, and a zero in a fare box
 * is a number a rider acts on.
 */
export function estimateFare(view: JourneyView): string | null {
  return view.estimate === null ? null : money(view.estimate.fare);
}

/**
 * The `Final` half of the design's fare box, which is **always** an absence today.
 *
 * TRP-01's trip carries no fare; PRC-02's `POST /v1/fares/calculate` is a computation needing a rate
 * card and trip measurements this application does not hold and must not assemble; and nothing
 * publishes a completed trip's charged total as a read. So `8 Complete`'s second figure and the
 * whole of `8b Fare differs` — `Final fare KES 2,290`, `KES 190 more than the estimate`, and the two
 * rows that explain it — have no source.
 *
 * A figure assembled here from a rate card would be money with no accounting behind it, which
 * `AGENTS.md` forbids in as many words. The two reasons are kept apart because only one of them is
 * somebody's to fix.
 */
export function finalFareCell(view: JourneyView): { value: string | null; because: string } {
  if (view.finalFare !== null) return { value: money(view.finalFare), because: "" };
  return { value: null, because: finalFareAbsenceWords(view.finalFareAbsence) };
}

/**
 * The difference between the estimate and the final fare, which `8b Fare differs` is entirely about.
 *
 * Returns nothing whenever either side is absent, and the absent side is always the final fare
 * today. Subtracting across two currencies is refused rather than coerced: two figures in different
 * currencies have no difference, and producing one would be the floating-point-money fault with an
 * extra step.
 */
export function fareDifference(view: JourneyView): Money | undefined {
  const estimate = view.estimate;
  const final = view.finalFare;
  if (estimate === null || final === null) return undefined;
  if (estimate.fare.currency !== final.currency) return undefined;
  return { currency: final.currency, amountMinor: final.amountMinor - estimate.fare.amountMinor };
}

/**
 * `Price held 4:12` — the countdown on `3 Quote`'s chip, against MOB-02's own expiry.
 *
 * Two instants and not one. `expiresAt` is MOB-02's, and `expired` is EXP-002's verdict against
 * **its own** clock rather than this phone's — which is the whole reason both are published. So the
 * countdown here is cosmetic and the verdict is not: a phone whose clock is four minutes fast must
 * not send a rider to `3b Quote expired` over a price that is still held, and a phone whose clock is
 * four minutes slow must not let them press Confirm on one that is not.
 *
 * `null` when the quote has expired, so the chip changes rather than counting into negatives.
 */
export function priceHeldLabel(expiresAt: string, now: number): string | null {
  const remaining = Date.parse(expiresAt) - now;
  if (!Number.isFinite(remaining) || remaining <= 0) return null;
  const seconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes)}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * Which layers of this answer did not answer, as one line a screen can put under its title.
 *
 * `degraded` is true when any layer that was read failed, and `sources` says which and why. A screen
 * that rendered a tidy page over a degraded answer would be telling a rider that what they are
 * looking at is the whole picture.
 */
export function degradedLayers(view: JourneyView): readonly string[] {
  return view.sources.filter((source) => source.status !== "ok").map((source) => source.service);
}

/**
 * Every scope a refused layer needed, so a `forbidden` state can name them.
 *
 * Read off the reports rather than from a list here, because the reports carry the scopes the
 * **upstream contract** publishes and a second list would go stale against it.
 */
export function refusedScopes(view: JourneyView): readonly string[] {
  return [
    ...new Set(
      view.sources
        .filter((source) => source.status === "forbidden" || source.status === "unauthorized")
        .flatMap((source) => source.scopes),
    ),
  ];
}
